import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  type Breakpoint,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import type { ReactNode, FC } from "react";
import { RADIUS } from "../../theme/mixins";

type CustomDialogProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  maxWidth?: Breakpoint;
};

const CustomDialog: FC<CustomDialogProps> = ({
  open,
  title,
  onClose,
  children,
  actions,
  maxWidth = "sm",
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      PaperProps={{
        sx: {
          borderRadius: `${RADIUS.cardLg}px`,
          boxShadow: theme.customShadows.modal,
        },
      }}
    >
      {/* Title with cross button */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "18px",
          fontWeight: 800,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.border.soft}`,
          py: 2.25,
        }}
      >
        {title}
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            backgroundColor: theme.palette.background.neutral,
            color: theme.palette.text.secondary,
            borderRadius: `${RADIUS.inputSm}px`,
            "&:hover": {
              backgroundColor: theme.palette.divider,
              color: theme.palette.text.primary,
            },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: theme.palette.border.soft }}>
        {children}
      </DialogContent>
      {actions && (
        <DialogActions
          sx={{
            p: "18px 24px",
            borderTop: `1px solid ${theme.palette.border.soft}`,
            background: theme.palette.surface.cyanHover,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CustomDialog;
